import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { SITE } from "@/content/site";
import { QualificationQuestion, formatReason } from "@/components/QualificationQuestion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-navy placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-200 focus:border-pool focus:ring-4 focus:ring-cyan-500/15";
const labelCls = "block text-sm font-semibold text-navy mb-1.5";

export const CampaignQuoteForm = ({ buttonLabel = "Get a Pool Service Quote" }) => {
  const [form, setForm] = useState({ name: "", address: "", phone: "", email: "", message: "" });
  const [reason, setReason] = useState({ choice: "", otherText: "" });
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const parts = [p.get("utm_source"), p.get("utm_medium"), p.get("utm_campaign")].filter(Boolean);
    setSource(parts.join(" · "));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
          address: form.address,
          service: "Weekly Pool Cleaning & Maintenance",
          message: form.message || null,
          source: source || null,
          reason: formatReason(reason),
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
      toast.success("Quote request sent — we'll be in touch shortly.");
    } catch {
      setStatus("idle");
      toast.error(`Something went wrong. Please call us at ${SITE.phone}.`);
    }
  };

  return (
    <div
      id="quote-form"
      data-testid="campaign-quote-form"
      className="relative rounded-2xl bg-white border border-slate-200/80 shadow-2xl shadow-navy/20 p-6 sm:p-8 scroll-mt-28"
    >
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            data-testid="campaign-form-success"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="py-12 text-center"
          >
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.15 }}
              className="mx-auto w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center"
            >
              <CheckCircle2 className="w-9 h-9 text-emerald-500" />
            </motion.span>
            <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-navy">
              Quote request received
            </h3>
            <p className="mt-3 text-sm sm:text-base text-slate-500 max-w-sm mx-auto">
              We&apos;ll review your pool&apos;s needs and reach out shortly. Need us sooner? Call{" "}
              <a href={SITE.phoneHref} data-testid="campaign-success-phone-link" className="font-semibold text-pool hover:text-ocean">
                {SITE.phone}
              </a>
              .
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -12 }}
            onSubmit={submit}
            className="space-y-4"
          >
            <div>
              <label htmlFor="campaign-name" className={labelCls}>Name *</label>
              <input id="campaign-name" data-testid="campaign-form-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} autoComplete="name" />
            </div>
            <div>
              <label htmlFor="campaign-address" className={labelCls}>Service Address *</label>
              <input id="campaign-address" data-testid="campaign-form-address-input" required value={form.address} onChange={set("address")} placeholder="Street address, Tierra Verde / 33715" className={inputCls} autoComplete="street-address" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="campaign-phone" className={labelCls}>Phone *</label>
                <input id="campaign-phone" data-testid="campaign-form-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(727) 555-0123" className={inputCls} autoComplete="tel" />
              </div>
              <div>
                <label htmlFor="campaign-email" className={labelCls}>Email *</label>
                <input id="campaign-email" data-testid="campaign-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} autoComplete="email" />
              </div>
            </div>
            <div>
              <label htmlFor="campaign-message" className={labelCls}>
                Message <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <textarea id="campaign-message" data-testid="campaign-form-message-input" rows={3} value={form.message} onChange={set("message")} placeholder="Anything helpful — pool size, salt system, screen enclosure…" className={`${inputCls} resize-none`} />
            </div>
            <QualificationQuestion value={reason} onChange={setReason} testidPrefix="campaign-form" />
            <button
              type="submit"
              data-testid="campaign-form-submit-button"
              disabled={status === "sending"}
              className="group w-full inline-flex items-center justify-center gap-2.5 bg-aqua text-navy font-bold px-8 py-4 rounded-full text-base transition-all duration-300 hover:bg-sky2 hover:text-navy hover:shadow-xl hover:shadow-cyan-500/25 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {status === "sending" ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</>
              ) : (
                <>{buttonLabel} <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" /></>
              )}
            </button>
            <p className="text-xs text-center text-slate-400">
              Serving Tierra Verde, St. Pete &amp; 33715 · No spam — ever.
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
